import React from "react";
import { Link } from "react-router-dom";
import { Download, ArrowLeft } from "lucide-react";
import { Seo } from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const ASSETS = [
    {
        key: "color",
        name: "Primary logo",
        desc: "Full-color logo with tagline. Use on light backgrounds.",
        svg: "/flexline-logo.svg",
        bg: "bg-[#FAF5FF]",
        previewH: "h-16",
    },
    {
        key: "white",
        name: "White logo",
        desc: "For dark backgrounds — footers, photos, dark UI.",
        svg: "/flexline-logo-white.svg",
        bg: "bg-espresso",
        previewH: "h-16",
    },
    {
        key: "wordmark",
        name: "Wordmark (compact)",
        desc: "Logo without tagline. Use in headers and tight spaces.",
        svg: "/flexline-wordmark.svg",
        bg: "bg-white",
        previewH: "h-12",
    },
    {
        key: "icon",
        name: "App icon",
        desc: "Square mark for app icons, avatars and favicons.",
        svg: "/favicon.svg",
        bg: "bg-white",
        previewH: "h-16",
    },
];

export default function Brand() {
    return (
        <div className="min-h-screen bg-cream" data-testid="brand-page">
            <Seo
                title="Brand & Logo Downloads | Flex Line"
                description="Download official Flex Line logos in SVG and PNG — primary, white, wordmark and app icon, in brand navy and red."
                path="/brand"
            />
            <header className="border-b border-[#DDD6FE] bg-paper/60 backdrop-blur sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <img src="/flexline-wordmark.svg" alt="Flex Line" className="h-7 w-auto" />
                    <Link to="/" className="text-sm font-mono link-underline flex items-center gap-1">
                        <ArrowLeft className="w-4 h-4" /> Home
                    </Link>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-6 py-14">
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">— Brand assets</p>
                <h1 className="font-display text-5xl font-light tracking-tight">The Flex Line logo</h1>
                <p className="text-zinc-600 mt-3 max-w-2xl">
                    Download official logos for your marketing, social profiles, business cards and partners.
                    SVG is vector (best quality, infinite scale); PNG is great for places that don&apos;t accept SVG.
                    Use <strong>JPG</strong> for upload spots that reject transparency (social profile photos, Google Business, some ad managers).
                </p>

                <div className="mt-10 grid sm:grid-cols-2 gap-5">
                    {ASSETS.map((a) => (
                        <div key={a.key} className="border border-[#DDD6FE] rounded-sm overflow-hidden bg-paper" data-testid={`brand-asset-${a.key}`}>
                            <div className={`${a.bg} h-40 grid place-items-center px-6`}>
                                <img src={a.svg} alt={a.name} className={`${a.previewH} w-auto`} />
                            </div>
                            <div className="p-5">
                                <div className="font-medium">{a.name}</div>
                                <div className="text-sm text-zinc-500 mt-1">{a.desc}</div>
                                <div className="flex flex-wrap gap-2 mt-4">
                                    <a href={a.svg} download
                                        data-testid={`download-svg-${a.key}`}
                                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 bg-espresso text-[#FAF5FF] hover:bg-black">
                                        <Download className="w-3.5 h-3.5" /> SVG
                                    </a>
                                    <a href={`${API}/api/brand/logo.png?variant=${a.key}&size=2000`}
                                        data-testid={`download-png-${a.key}`}
                                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 border border-[#DDD6FE] bg-white hover:bg-cream">
                                        <Download className="w-3.5 h-3.5" /> PNG · 2000px
                                    </a>
                                    <a href={`${API}/api/brand/logo.png?variant=${a.key}&size=512`}
                                        data-testid={`download-png-sm-${a.key}`}
                                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 border border-[#DDD6FE] bg-white hover:bg-cream">
                                        <Download className="w-3.5 h-3.5" /> PNG · 512px
                                    </a>
                                    <a href={`${API}/api/brand/logo.png?variant=${a.key}&size=2000&format=jpeg`}
                                        data-testid={`download-jpg-${a.key}`}
                                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 border border-[#DDD6FE] bg-white hover:bg-cream">
                                        <Download className="w-3.5 h-3.5" /> JPG · 2000px
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 border border-[#DDD6FE] rounded-sm bg-paper p-6">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Brand colors</p>
                    <div className="flex flex-wrap gap-4">
                        {[["Navy", "#4C1D95"], ["Red", "#D97706"], ["Espresso", "#2B2118"], ["Cream", "#FAF5FF"]].map(([n, hex]) => (
                            <div key={hex} className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-sm border border-[#DDD6FE]" style={{ background: hex }} />
                                <div className="text-xs">
                                    <div className="font-medium">{n}</div>
                                    <div className="font-mono text-zinc-500">{hex}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
